import { useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { useSession } from '../lib/auth-client';
import { api, ApiError, type ApiSuccess } from '../lib/api';
import { LocationPicker } from '../components/LocationPicker';
import { ImageUpload } from '../components/ImageUpload';
import { SAUDI_CITIES, getDistrictsForCity } from '../lib/saudi-locations';

const TYPES = [
  { id: 'VILLA',      label: '🏠 فيلا' },
  { id: 'APARTMENT',  label: '🏢 شقة' },
  { id: 'LAND',       label: '🏗️ أرض' },
  { id: 'COMMERCIAL', label: '🏪 تجاري' },
  { id: 'OFFICE',     label: '💼 مكتب' },
  { id: 'BUILDING',   label: '🏬 عمارة' },
  { id: 'FARM',       label: '🌳 مزرعة' },
  { id: 'WAREHOUSE',  label: '📦 مستودع' },
  { id: 'REST_HOUSE', label: '🌴 استراحة' },
] as const;

const FEATURES_OPTIONS = [
  { id: 'pool',         label: '💧 مسبح' },
  { id: 'garden',       label: '🌳 حديقة' },
  { id: 'majlis',       label: '🛋️ مجلس رجال' },
  { id: 'women_majlis', label: '💃 مجلس نساء' },
  { id: 'maid_room',    label: '🛏️ غرفة خادمة' },
  { id: 'driver_room',  label: '🚗 غرفة سائق' },
  { id: 'basement',     label: '🚪 قبو' },
  { id: 'roof_room',    label: '🌅 غرفة سطح' },
  { id: 'parking',      label: '🅿️ موقف سيارات' },
  { id: 'central_ac',   label: '❄️ تكييف مركزي' },
  { id: 'elevator',     label: '🛗 مصعد' },
  { id: 'security',     label: '🛡️ أمن وحراسة' },
];

export function SubmitPropertyPage() {
  const navigate = useNavigate();
  const { data: session, isPending } = useSession();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  const [form, setForm] = useState({
    type: '' as typeof TYPES[number]['id'] | '',
    purpose: '' as 'SALE' | 'RENT' | 'INVESTMENT' | '',
    title: '',
    description: '',
    price: '',
    area: '',
    rooms: '0',
    bathrooms: '0',
    floors: '1',
    ageYears: '0',
    streetWidth: '',
    facade: '',
    city: '',
    district: '',
    address: '',
    latitude: null as number | null,
    longitude: null as number | null,
    coverImage: '',
    images: [''] as string[],
    features: [] as string[],
    deedNumber: '',
  });

  const submit = useMutation({
    mutationFn: () => api.post<ApiSuccess<{ id: string; status: string }>>('/api/properties', {
      type: form.type,
      purpose: form.purpose,
      title: form.title.trim(),
      description: form.description.trim() || undefined,
      price: Number(form.price),
      area: Number(form.area),
      rooms: Number(form.rooms),
      bathrooms: Number(form.bathrooms),
      floors: Number(form.floors),
      ageYears: Number(form.ageYears),
      streetWidth: Number(form.streetWidth) || 0,
      facade: form.facade || undefined,
      city: form.city,
      district: form.district || undefined,
      address: form.address || undefined,
      latitude: form.latitude ?? undefined,
      longitude: form.longitude ?? undefined,
      coverImage: form.coverImage || undefined,
      images: form.images.filter((u) => u.trim().length > 0),
      features: form.features,
      deedNumber: form.deedNumber || undefined,
    }),
  });

  if (isPending) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin text-3xl text-navy">⟳</div></div>;
  }
  if (!session?.user) {
    return <Navigate to="/login?redirect=/submit-property" replace />;
  }

  const role = (session.user as { role?: string }).role ?? 'USER';
  const isAdmin = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const isBroker = role === 'BROKER';
  // 🔒 كل الطلبات تمر عبر مراجعة الإدارة — حتى الأدمن والمسوّق (للجودة والمراجعة)
  const willPublishImmediately = false;

  // 🪪 جلب حالة رخصة فال (لمنع الإرسال لو غير سارية للمستخدم العادي)
  const { data: falLicense } = useQuery({
    queryKey: ['me', 'fal-license-validity'],
    queryFn: () => api.get<ApiSuccess<{ validity: { ok: boolean; status: string | null; reason: string | null } }>>('/api/me/fal-license/me'),
    enabled: !isAdmin,  // الأدمن معفى
  });
  const falOk = isAdmin || (falLicense?.data?.validity?.ok ?? false);
  const falStatus = falLicense?.data?.validity?.status ?? null;
  const falReason = falLicense?.data?.validity?.reason ?? null;

  // 🪪 تحقّق نفاذ — متطلب الهيئة العامة للعقار للمعلنين
  const { data: nafath, isLoading: nafathLoading } = useQuery({
    queryKey: ['me', 'nafath'],
    queryFn: () => api.get<ApiSuccess<{ verified: boolean; verifiedAt: string | null; nationalIdMasked: string | null }>>('/api/nafath/me'),
    enabled: !isAdmin,
  });
  const nafathVerified = isAdmin || (nafath?.data?.verified ?? false);

  // ⛔ بوّابة نفاذ — إذا لم يتحقق، اعرض دعوة التحقق
  if (!isAdmin && !nafathLoading && !nafathVerified) {
    return (
      <div className="bg-gradient-to-b from-navy-soft/30 to-white min-h-[70vh] py-12 px-4">
        <Card className="max-w-md mx-auto"><CardBody className="text-center py-8">
          <div className="text-6xl mb-3">🪪</div>
          <h1 className="text-2xl font-black text-navy-dark mb-2">يلزم التحقق بنفاذ</h1>
          <p className="text-sm text-gray-600 leading-relaxed mb-5">
            وفق متطلبات <b>الهيئة العامة للعقار</b>، يجب توثيق هوية المُعلِن العقاري عبر نظام
            <b> نفاذ الوطني </b> قبل نشر أي إعلان. لن يحتاج باقي المستخدمين هذه الخطوة.
          </p>
          <button
            onClick={() => navigate('/verify-nafath?next=/submit-property')}
            className="w-full bg-navy text-white py-3 rounded-xl font-black hover:bg-navy-dark transition"
          >
            التحقق بنفاذ الآن ←
          </button>
        </CardBody></Card>
      </div>
    );
  }

  const setField = <K extends keyof typeof form>(k: K, v: typeof form[K]) => setForm((s) => ({ ...s, [k]: v }));
  const districts = [...new Set(getDistrictsForCity(form.city))];

  // ━━━ تحقق تفصيلي بكل خانة + قائمة بالأخطاء لعرضها ━━━
  function validateStep(s: 1 | 2 | 3): { ok: boolean; missing: string[] } {
    const missing: string[] = [];
    if (s === 1) {
      if (!form.type)                       missing.push('نوع العقار');
      if (!form.purpose)                    missing.push('الغرض (للبيع/إيجار/استثمار)');
      if (form.title.trim().length < 5)     missing.push('عنوان الإعلان (5 أحرف فأكثر)');
      if (!Number(form.price) || Number(form.price) <= 0)  missing.push('السعر (أكبر من صفر)');
      if (!Number(form.area)  || Number(form.area)  <= 0)  missing.push('المساحة (م²)');
    } else if (s === 2) {
      if (!form.city) missing.push('المدينة');
    }
    return { ok: missing.length === 0, missing };
  }
  const step1 = validateStep(1);
  const step2 = validateStep(2);
  const step3 = validateStep(3);

  // ━━━ منع الـ Tab Click على خطوات لاحقة لو خطوات قبلها غير مكتملة ━━━
  const canGoToStep = (target: 1 | 2 | 3 | 4): boolean => {
    if (target <= step) return true; // الرجوع دائماً مسموح
    if (target >= 2 && !step1.ok) return false;
    if (target >= 3 && !step2.ok) return false;
    if (target >= 4 && !step3.ok) return false;
    return true;
  };

  // ━━━ تنسيق رسالة الخطأ من الـ API ━━━
  const formatApiError = (err: unknown): string => {
    if (err instanceof ApiError) {
      // لو فيه أخطاء حقول من الـ backend، اعرضها
      if (err.fieldErrors && Object.keys(err.fieldErrors).length > 0) {
        const lines = Object.entries(err.fieldErrors).map(([field, msgs]) =>
          `• ${FIELD_LABELS[field] ?? field}: ${msgs.join('، ')}`,
        );
        return lines.join('\n');
      }
      return err.message;
    }
    if (err instanceof Error) return err.message;
    return 'حدث خطأ غير متوقع';
  };

  // 🎯 شاشة النجاح
  if (submit.isSuccess) {
    const result = submit.data?.data;
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Card>
          <CardBody className="text-center py-12">
            <div className="text-7xl mb-4">✅</div>
            <h1 className="text-3xl font-black text-navy-dark mb-3">تم استلام طلبك</h1>
            <p className="text-ink-secondary mb-2 leading-relaxed">
              سيراجع فريقنا عقارك خلال 24 ساعة، وستصلك رسائل بكل خطوة (الاستلام، المراجعة، الموافقة/الرفض) عبر الإيميل والإشعارات.
            </p>
            {result?.id && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 my-5 text-xs">
                <div className="text-ink-muted mb-1">رقم الطلب</div>
                <code className="font-mono text-navy-dark font-bold">#{result.id.slice(-12).toUpperCase()}</code>
              </div>
            )}
            <div className="flex flex-wrap gap-2 justify-center mt-6">
              <Link to="/dashboard/properties">
                <Button variant="primary">📋 عقاراتي</Button>
              </Link>
              <Link to="/properties">
                <Button variant="ghost">🏘️ تصفّح العقارات</Button>
              </Link>
              <button
                onClick={() => { submit.reset(); setStep(1); setForm({ ...form, title: '', description: '', price: '', area: '' }); }}
                className="px-4 py-2 rounded-xl text-sm font-bold text-navy-dark hover:bg-gray-100"
              >
                ➕ إضافة عقار آخر
              </button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* 🎯 رأس */}
      <div className="text-center mb-6">
        <h1 className="text-3xl md:text-4xl font-black text-navy-dark mb-2">🏠 طلب إضافة عقار جديد</h1>
        <p className="text-ink-secondary">
          أدخل تفاصيل عقارك — سيراجعه الفريق ويُنشره خلال 24 ساعة بعد الموافقة.
        </p>
        <Badge color="amber">⏳ يحتاج مراجعة الإدارة قبل النشر</Badge>
      </div>

      {/* 🪪 تنبيه رخصة فال — يمنع الإكمال لو الرخصة غير سارية */}
      {!falOk && falLicense && (
        <Card className="mb-5 border-2 border-red-300 bg-red-50/50">
          <CardBody>
            <div className="flex items-start gap-3 flex-wrap">
              <div className="text-4xl shrink-0">🪪</div>
              <div className="flex-1 min-w-0">
                <h3 className="font-black text-red-800 mb-1">
                  {falStatus === 'NONE' ? 'لم ترفع رخصة فال بعد' :
                   falStatus === 'PENDING' ? 'رخصتك قيد المراجعة' :
                   falStatus === 'REJECTED' ? 'رخصتك مرفوضة — تحتاج تحديث' :
                   falStatus === 'EXPIRED' ? 'رخصة فال منتهية' :
                   'لا يمكن نشر العقار'}
                </h3>
                <p className="text-xs text-red-700 mb-3 leading-relaxed">
                  {falReason ?? 'لنشر العقارات على المنصة، يجب أن تكون لديك رخصة فال سارية ومعتمدة من الهيئة العامة للعقار.'}
                </p>
                <Link to="/dashboard/profile">
                  <Button variant="primary" size="sm">
                    {falStatus === 'NONE' ? '📤 رفع رخصة فال' :
                     falStatus === 'EXPIRED' || falStatus === 'REJECTED' ? '🔄 تحديث الرخصة' :
                     '👁️ عرض حالة الرخصة'}
                  </Button>
                </Link>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* مؤشّر الخطوات */}
      <Card className="mb-5">
        <CardBody className="!py-3">
          <div className="flex items-center justify-between gap-1">
            {[
              { n: 1, label: 'الأساسيات', icon: '📝' },
              { n: 2, label: 'الموقع', icon: '📍' },
              { n: 3, label: 'الصور والمزايا', icon: '🖼️' },
              { n: 4, label: 'مراجعة وإرسال', icon: '✓' },
            ].map((s, i, arr) => (
              <div key={s.n} className="flex items-center flex-1">
                <button
                  onClick={() => canGoToStep(s.n as 1 | 2 | 3 | 4) && setStep(s.n as 1 | 2 | 3 | 4)}
                  className={`flex flex-col items-center gap-1 shrink-0 ${s.n <= step ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                  disabled={s.n > step}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition border-2 ${
                    step === s.n ? 'bg-gold text-white border-gold scale-110' :
                    step > s.n ? 'bg-navy text-white border-navy' :
                    'bg-gray-100 text-gray-400 border-gray-200'
                  }`}>
                    {step > s.n ? '✓' : s.icon}
                  </div>
                  <span className={`text-[10px] font-bold ${step === s.n ? 'text-gold' : step > s.n ? 'text-navy-dark' : 'text-gray-400'}`}>
                    {s.label}
                  </span>
                </button>
                {i < arr.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${step > s.n ? 'bg-navy' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* 📝 الخطوة 1: الأساسيات */}
      {step === 1 && (
        <Card>
          <CardBody>
            <h2 className="text-xl font-black text-navy-dark mb-4">📝 معلومات أساسية</h2>

            <div className="mb-4">
              <label className="block text-sm font-bold text-ink mb-2">نوع العقار *</label>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                {TYPES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setField('type', t.id)}
                    className={`p-2.5 rounded-xl border-2 text-center text-xs font-bold transition ${
                      form.type === t.id ? 'border-navy bg-navy-soft text-navy-dark' : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-bold text-ink mb-2">الغرض *</label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <button
                  onClick={() => setField('purpose', 'SALE')}
                  className={`p-3 rounded-xl border-2 text-center font-bold transition ${
                    form.purpose === 'SALE' ? 'border-gold bg-gold-soft text-gold-dark' : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  💰 للبيع
                </button>
                <button
                  onClick={() => setField('purpose', 'RENT')}
                  className={`p-3 rounded-xl border-2 text-center font-bold transition ${
                    form.purpose === 'RENT' ? 'border-navy bg-navy-soft text-navy-dark' : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  🔑 للإيجار
                </button>
                <button
                  onClick={() => setField('purpose', 'INVESTMENT')}
                  className={`p-3 rounded-xl border-2 text-center font-bold transition ${
                    form.purpose === 'INVESTMENT' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  📈 للاستثمار
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 mb-4">
              <Input
                label="عنوان الإعلان *"
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
                placeholder="مثل: فيلا فاخرة في حي الرياض الجنوبي — 4 غرف"
                hint="عنوان جذّاب يصف العقار باختصار (5 أحرف فأكثر)"
              />
              <div>
                <label className="block text-sm font-bold text-ink mb-1">الوصف</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
                  placeholder="اشرح مزايا العقار، الموقع، التشطيبات، أي معلومة تساعد المشتري…"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Input label={`السعر (ر.س) *`} type="number" value={form.price} onChange={(e) => setField('price', e.target.value)} />
              <Input label="المساحة (م²) *" type="number" value={form.area} onChange={(e) => setField('area', e.target.value)} />
              <Input label="عمر العقار (سنة)" type="number" value={form.ageYears} onChange={(e) => setField('ageYears', e.target.value)} />
              <Input label="الواجهة" value={form.facade} onChange={(e) => setField('facade', e.target.value)} placeholder="شمالية، جنوبية..." />
            </div>

            {(form.type === 'VILLA' || form.type === 'APARTMENT' || form.type === 'BUILDING' || form.type === 'REST_HOUSE') && (
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Input label="غرف النوم" type="number" value={form.rooms} onChange={(e) => setField('rooms', e.target.value)} />
                <Input label="دورات المياه" type="number" value={form.bathrooms} onChange={(e) => setField('bathrooms', e.target.value)} />
                <Input label="عدد الأدوار" type="number" value={form.floors} onChange={(e) => setField('floors', e.target.value)} />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mb-4">
              <Input label="عرض الشارع (متر)" type="number" value={form.streetWidth} onChange={(e) => setField('streetWidth', e.target.value)} />
              <Input label="رقم الصك (اختياري)" value={form.deedNumber} onChange={(e) => setField('deedNumber', e.target.value)} dir="ltr" />
            </div>

            {/* 🚨 تنبيه: الحقول الناقصة لمنع التقدّم */}
            {!step1.ok && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 mt-5 text-xs text-amber-900">
                <div className="font-bold mb-1">⚠️ يجب تعبئة الحقول التالية قبل المتابعة:</div>
                <ul className="space-y-0.5 mr-4">
                  {step1.missing.map((m) => <li key={m}>• {m}</li>)}
                </ul>
              </div>
            )}

            <div className="flex justify-end mt-5 pt-5 border-t">
              <Button variant="primary" onClick={() => step1.ok && setStep(2)} disabled={!step1.ok}>
                التالي: الموقع →
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* 📍 الخطوة 2: الموقع */}
      {step === 2 && (
        <Card>
          <CardBody>
            <h2 className="text-xl font-black text-navy-dark mb-4">📍 موقع العقار</h2>
            <p className="text-xs text-ink-muted mb-4">حدد المدينة والحي بدقة، ثم اختر الموقع على الخريطة (اختياري لكنه يساعد المشتري كثيراً).</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-sm font-bold text-ink mb-1">المدينة *</label>
                <select
                  value={form.city}
                  onChange={(e) => setField('city', e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white"
                >
                  <option value="">— اختر المدينة —</option>
                  {SAUDI_CITIES.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-ink mb-1">الحي</label>
                <select
                  value={form.district}
                  onChange={(e) => setField('district', e.target.value)}
                  disabled={!form.city}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white disabled:bg-gray-100"
                >
                  <option value="">{form.city ? '— اختر الحي —' : 'اختر المدينة أولاً'}</option>
                  {districts.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <Input label="العنوان التفصيلي (اختياري)" value={form.address} onChange={(e) => setField('address', e.target.value)} placeholder="اسم الشارع، رقم القطعة..." />
              </div>
            </div>

            <div className="mt-4">
              <LocationPicker
                label="📍 موقع العقار على الخريطة (اختياري — يساعدنا في تعبئة المدينة والحي تلقائياً)"
                value={{ lat: form.latitude, lng: form.longitude, address: form.address }}
                onChange={(v) => setForm((s) => ({
                  ...s,
                  latitude: v.lat,
                  longitude: v.lng,
                  address: v.address ?? s.address,
                  // ✨ ملء المدينة والحي تلقائياً إذا اكتشفناها من الـ reverse geocode
                  city: v.detectedCity ?? s.city,
                  district: v.detectedDistrict ?? s.district,
                }))}
                city={form.city}
                height={350}
              />
              {form.latitude && form.longitude && (form.city || form.district) && (
                <div className="mt-2 bg-success-bg border border-success/30 rounded-lg p-2 text-xs text-success-dark">
                  ✓ تم استخراج <b>{form.city}{form.district ? ` · ${form.district}` : ''}</b> تلقائياً من الموقع.
                  يمكنك تعديل الحقول أعلاه إذا لزم.
                </div>
              )}
            </div>

            {/* 🚨 تنبيه الحقول الناقصة */}
            {!step2.ok && (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-3 mt-5 text-xs text-amber-900">
                <div className="font-bold mb-1">⚠️ يجب تعبئة الحقول التالية:</div>
                <ul className="space-y-0.5 mr-4">
                  {step2.missing.map((m) => <li key={m}>• {m}</li>)}
                </ul>
              </div>
            )}

            <div className="flex justify-between mt-5 pt-5 border-t">
              <Button variant="ghost" onClick={() => setStep(1)}>← السابق</Button>
              <Button variant="primary" onClick={() => step2.ok && setStep(3)} disabled={!step2.ok}>
                التالي: الصور والمزايا →
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* 🖼️ الخطوة 3: الصور والمزايا */}
      {step === 3 && (
        <Card>
          <CardBody>
            <h2 className="text-xl font-black text-navy-dark mb-4">🖼️ الصور والمزايا</h2>

            {/* 📸 رفع الصور (مباشرة من الجهاز) */}
            <div className="mb-6">
              <ImageUpload
                label="📸 صور العقار"
                hint="ارفع 1-10 صور عالية الجودة. الصورة الأولى ستكون صورة الغلاف (يمكنك إعادة الترتيب لاحقاً)."
                value={[
                  ...(form.coverImage ? [form.coverImage] : []),
                  ...form.images.filter((u) => u.trim().length > 0),
                ]}
                onChange={(urls) => {
                  // الأولى = cover، الباقي = images
                  setForm((s) => ({
                    ...s,
                    coverImage: urls[0] ?? '',
                    images: urls.slice(1),
                  }));
                }}
                maxImages={10}
                purpose="PROPERTY_IMAGE"
              />
            </div>

            <div className="mb-5">
              <label className="block text-sm font-bold text-ink mb-2">✨ المزايا (اختر ما يناسب)</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {FEATURES_OPTIONS.map((f) => {
                  const on = form.features.includes(f.id);
                  return (
                    <button
                      key={f.id}
                      onClick={() => setField('features', on ? form.features.filter((x) => x !== f.id) : [...form.features, f.id])}
                      className={`p-2.5 rounded-xl border-2 text-right text-xs font-bold transition ${
                        on ? 'border-gold bg-gold-soft text-gold-dark' : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      {on ? '✓ ' : ''}{f.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-between mt-5 pt-5 border-t">
              <Button variant="ghost" onClick={() => setStep(2)}>← السابق</Button>
              <Button variant="primary" onClick={() => step3.ok && setStep(4)} disabled={!step3.ok}>
                التالي: مراجعة وإرسال →
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* ✓ الخطوة 4: مراجعة وإرسال */}
      {step === 4 && (
        <Card>
          <CardBody>
            <h2 className="text-xl font-black text-navy-dark mb-4">✓ مراجعة قبل الإرسال</h2>

            <div className="space-y-4">
              <Section title="📝 الأساسيات">
                <Row label="النوع" value={TYPES.find((t) => t.id === form.type)?.label ?? '—'} />
                <Row label="الغرض" value={
                  form.purpose === 'SALE' ? '💰 للبيع' :
                  form.purpose === 'RENT' ? '🔑 للإيجار' :
                  form.purpose === 'INVESTMENT' ? '📈 للاستثمار' : '—'
                } />
                <Row label="العنوان" value={form.title} />
                <Row label="السعر" value={`${Number(form.price).toLocaleString('en-US')} ر.س`} highlight />
                <Row label="المساحة" value={`${form.area} م²`} />
                {Number(form.rooms) > 0 && <Row label="غرف/حمامات/أدوار" value={`🛏 ${form.rooms} · 🚿 ${form.bathrooms} · 🏢 ${form.floors}`} />}
              </Section>

              <Section title="📍 الموقع">
                <Row label="المدينة" value={form.city} />
                {form.district && <Row label="الحي" value={form.district} />}
                {form.address && <Row label="العنوان التفصيلي" value={form.address} />}
                {form.latitude && form.longitude && (
                  <Row label="الإحداثيات" value={`${form.latitude.toFixed(5)}, ${form.longitude.toFixed(5)}`} />
                )}
              </Section>

              <Section title="🖼️ الصور والمزايا">
                <Row label="الصورة الرئيسية" value={form.coverImage ? '✓ موجودة' : '— افتراضية'} />
                <Row label="صور إضافية" value={`${form.images.filter((u) => u.trim()).length} صورة`} />
                <Row label="المزايا" value={form.features.length > 0 ? `${form.features.length} ميزة` : '— بدون'} />
              </Section>
            </div>

            {/* 🚨 منع الإرسال لو فيه أي خطوة ناقصة — مع رسالة توضيحية */}
            {(!step1.ok || !step2.ok) && (
              <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3 mt-4 text-xs text-amber-900">
                <div className="font-bold mb-2">⚠️ لا يمكن إرسال الطلب — حقول ناقصة في الخطوات السابقة:</div>
                {!step1.ok && (
                  <div className="mb-2">
                    <button onClick={() => setStep(1)} className="text-amber-700 hover:underline font-bold">📝 الخطوة 1: المعلومات الأساسية</button>
                    <ul className="mr-4 mt-1">
                      {step1.missing.map((m) => <li key={m}>• {m}</li>)}
                    </ul>
                  </div>
                )}
                {!step2.ok && (
                  <div>
                    <button onClick={() => setStep(2)} className="text-amber-700 hover:underline font-bold">📍 الخطوة 2: الموقع</button>
                    <ul className="mr-4 mt-1">
                      {step2.missing.map((m) => <li key={m}>• {m}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {submit.isError && (
              <div className="bg-red-50 border-2 border-red-200 text-red-700 text-sm rounded-xl p-4 mt-4">
                <div className="font-bold mb-1 flex items-center gap-1">⚠️ تعذّر إرسال الطلب</div>
                <div className="text-xs whitespace-pre-line leading-relaxed">{formatApiError(submit.error)}</div>
                <div className="text-[11px] text-red-600 mt-2">راجع البيانات في الخطوات السابقة وحاول مرة أخرى.</div>
              </div>
            )}

            <div className="bg-info-bg border border-info/30 rounded-xl p-3 mt-4 text-xs text-info-dark">
              ℹ️ بإرسال هذا النموذج، فأنت تؤكّد أن البيانات صحيحة وأن العقار مملوك لك أو لديك تفويض ببيعه/تأجيره.
              {!willPublishImmediately && ' سيراجع الفريق طلبك خلال 24 ساعة.'}
            </div>

            <div className="flex justify-between mt-5 pt-5 border-t">
              <Button variant="ghost" onClick={() => setStep(3)}>← السابق</Button>
              <Button
                variant="primary"
                size="lg"
                onClick={() => submit.mutate()}
                loading={submit.isPending}
                disabled={!step1.ok || !step2.ok || !falOk}
              >
                {!falOk ? '🪪 يحتاج رخصة فال سارية' : '📤 إرسال طلب الإضافة'}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━ Field Labels (لتنسيق رسائل الخطأ من الـ API) ━━━━━━━━━━━━━━━━━━━━

const FIELD_LABELS: Record<string, string> = {
  type: 'نوع العقار',
  purpose: 'الغرض',
  title: 'عنوان الإعلان',
  description: 'الوصف',
  price: 'السعر',
  area: 'المساحة',
  rooms: 'غرف النوم',
  bathrooms: 'دورات المياه',
  floors: 'عدد الأدوار',
  ageYears: 'عمر العقار',
  streetWidth: 'عرض الشارع',
  facade: 'الواجهة',
  city: 'المدينة',
  district: 'الحي',
  address: 'العنوان التفصيلي',
  latitude: 'خط العرض',
  longitude: 'خط الطول',
  coverImage: 'الصورة الرئيسية',
  images: 'الصور',
  features: 'المزايا',
  deedNumber: 'رقم الصك',
};

// ━━━━━━━━━━━━━━━━━━━━ Helpers ━━━━━━━━━━━━━━━━━━━━

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="border border-gray-200 rounded-xl p-4">
      <legend className="px-2 font-black text-navy-dark text-sm">{title}</legend>
      <div className="space-y-1.5 mt-2">{children}</div>
    </fieldset>
  );
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex justify-between items-center text-sm">
      <span className="text-ink-muted text-xs">{label}:</span>
      <span className={`font-bold ${highlight ? 'text-gold-dark text-base' : 'text-ink'}`}>{value}</span>
    </div>
  );
}
